import * as React from "react";

/**
 * Dane strony dla mobilnego paska gornego (<md). `PageHeader` na telefonie nie
 * renderuje tytulu pod paskiem, tylko rejestruje go tutaj — pasek pokazuje
 * jeden tytul, ulubione i info. Bez providera (testy, tryb `embedded`)
 * rejestracja jest no-opem i `PageHeader` renderuje sie jak na desktopie.
 *
 * Stan i settery sa w osobnych kontekstach: `PageHeader` czyta tylko stabilne
 * settery, wiec rejestracja (takze JSX w `infoTooltip`) nie renderuje go
 * ponownie i nie zapetla efektu.
 */
export interface PageChrome {
	title: string;
	/** Href ulubionych (domyslnie biezaca sciezka). */
	favoriteHref: string;
	infoTooltip?: React.ReactNode;
}

/**
 * Sekcja popovera ikony info na telefonie (legenda tabeli, aktualnosc danych),
 * ktora na desktopie stoi nad tabela. Rejestruje ja `usePageNote`.
 */
export interface PageNote {
	id: string;
	title: string;
	content: React.ReactNode;
}

interface PageChromeState {
	chrome: PageChrome | null;
	notes: PageNote[];
	debugMonitor: boolean;
}

interface PageChromeActions {
	setChrome: (chrome: PageChrome | null) => void;
	/** `null` usuwa sekcje; istniejaca sekcja zmienia tresc w miejscu. */
	setNote: (id: string, note: Omit<PageNote, "id"> | null) => void;
	setDebugMonitor: (value: boolean) => void;
}

const PageChromeStateContext = React.createContext<PageChromeState | null>(null);
const PageChromeActionsContext = React.createContext<PageChromeActions | null>(null);

export function PageChromeProvider({ children }: { children: React.ReactNode }) {
	const [chrome, setChrome] = React.useState<PageChrome | null>(null);
	const [notes, setNotes] = React.useState<PageNote[]>([]);
	const [debugMonitor, setDebugMonitor] = React.useState(false);
	const setNote = React.useCallback((id: string, note: Omit<PageNote, "id"> | null) => {
		setNotes((prev) => {
			const index = prev.findIndex((n) => n.id === id);
			if (note == null) return index < 0 ? prev : prev.filter((n) => n.id !== id);
			const entry = { id, ...note };
			if (index < 0) return [...prev, entry];
			const next = prev.slice();
			next[index] = entry;
			return next;
		});
	}, []);
	const state = React.useMemo(
		() => ({ chrome, notes, debugMonitor }),
		[chrome, notes, debugMonitor],
	);
	const actions = React.useMemo(() => ({ setChrome, setNote, setDebugMonitor }), [setNote]);
	return (
		<PageChromeActionsContext.Provider value={actions}>
			<PageChromeStateContext.Provider value={state}>{children}</PageChromeStateContext.Provider>
		</PageChromeActionsContext.Provider>
	);
}

export function usePageChromeState(): PageChromeState | null {
	return React.useContext(PageChromeStateContext);
}

export function usePageChromeActions(): PageChromeActions | null {
	return React.useContext(PageChromeActionsContext);
}

/**
 * Rejestruje tytul strony w pasku. Zwraca true, gdy pasek go przejal (telefon
 * z providerem) — wtedy wywolujacy nie renderuje wlasnego tytulu.
 */
export function useRegisterPageChrome(chrome: PageChrome, enabled: boolean): boolean {
	const setChrome = React.useContext(PageChromeActionsContext)?.setChrome;
	const { title, favoriteHref, infoTooltip } = chrome;
	React.useEffect(() => {
		if (!enabled || !setChrome) return;
		setChrome({ title, favoriteHref, infoTooltip });
		return () => setChrome(null);
	}, [enabled, setChrome, title, favoriteHref, infoTooltip]);
	return enabled && !!setChrome;
}

/**
 * Rejestruje sekcje popovera info w pasku. Zwraca true, gdy pasek ja przejal
 * (telefon z providerem) — wtedy wywolujacy nie renderuje jej nad tabela.
 * Tresc aktualizuje sie w miejscu (bez zdejmowania), wiec kolejnosc sekcji
 * nie skacze przy kazdym renderze strony; zdjecie nastepuje przy odmontowaniu.
 */
export function usePageNote(
	id: string,
	title: string,
	content: React.ReactNode,
	enabled: boolean,
): boolean {
	const setNote = React.useContext(PageChromeActionsContext)?.setNote;
	React.useEffect(() => {
		if (enabled && setNote) setNote(id, { title, content });
	}, [enabled, setNote, id, title, content]);
	React.useEffect(() => {
		if (!enabled || !setNote) return;
		return () => setNote(id, null);
	}, [enabled, setNote, id]);
	return enabled && !!setNote;
}
